/**
 * Error normalizer for converting various API error formats to ApiError.
 *
 * Handles three error formats:
 * 1. FCC: { success: false, error: string, message: string }
 * 2. Common Merchant: { Code: string, Message: string, FieldErrors?: [...] }
 * 3. Tax Injector: { Code: string, Message: string, CsvRowErrors?: [...] }
 */

import { ApiError, type FieldError, type ApiErrorDetails } from './ApiError'
import type { AxiosError } from 'axios'

/**
 * FCC API error response format
 * Detected by: success === false
 */
interface FCCErrorFormat {
  success: false
  error: string
  message: string
  statusCode?: number
}

/**
 * Common Merchant API error response format
 * Detected by: has Code and Message properties (without CsvRowErrors)
 * FieldErrors can be array of { Field, Message, Code } or Record<fieldName, message>.
 */
interface CommonMerchantErrorFormat {
  Code: string
  Message: string
  FieldErrors?:
    | Array<{
        Field: string
        Code?: string
        Message: string
      }>
    | Record<string, string>
}

/**
 * CSV field error from Tax Injector
 */
interface CsvFieldError {
  Field: string
  Message: string
  Code?: string
}

/**
 * CSV row error from Tax Injector
 */
interface CsvRowError {
  RowNumber: number
  FieldErrors: CsvFieldError[]
}

/**
 * Tax Injector validation error response format
 * Detected by: has CsvRowErrors property
 */
interface TaxInjectorErrorFormat {
  Code: string
  Message: string
  CsvRowErrors?: CsvRowError[]
}

/**
 * Check if error is FCC format
 */
function isFCCError(error: unknown): error is FCCErrorFormat {
  return (
    typeof error === 'object' &&
    error !== null &&
    'success' in error &&
    (error as FCCErrorFormat).success === false &&
    'message' in error
  )
}

/**
 * Check if error is Tax Injector format (has CsvRowErrors)
 */
function isTaxInjectorError(error: unknown): error is TaxInjectorErrorFormat {
  return (
    typeof error === 'object' &&
    error !== null &&
    'Code' in error &&
    'Message' in error &&
    'CsvRowErrors' in error &&
    Array.isArray((error as TaxInjectorErrorFormat).CsvRowErrors)
  )
}

/**
 * Check if error is Common Merchant format
 */
function isCommonMerchantError(error: unknown): error is CommonMerchantErrorFormat {
  return (
    typeof error === 'object' &&
    error !== null &&
    'Code' in error &&
    'Message' in error &&
    !('CsvRowErrors' in error)
  )
}

/**
 * Check if error is an Axios error
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  )
}

/**
 * Normalize FCC error format to ApiErrorDetails
 */
function normalizeFCCError(error: FCCErrorFormat): ApiErrorDetails {
  return {
    message: error.message || error.error || 'An error occurred',
    code: error.error,
    statusCode: error.statusCode,
    originalResponse: error,
  }
}

/**
 * Normalize Common Merchant error format to ApiErrorDetails.
 * Supports FieldErrors as either array of { Field, Message, Code } or Record<string, string>.
 */
function normalizeCommonMerchantError(error: CommonMerchantErrorFormat): ApiErrorDetails {
  let fieldErrors: FieldError[] | undefined
  const raw = error.FieldErrors
  if (Array.isArray(raw)) {
    fieldErrors = raw.map(fe => ({
      field: fe.Field,
      message: fe.Message,
      code: fe.Code,
    }))
  } else if (raw && typeof raw === 'object') {
    fieldErrors = Object.entries(raw).map(([field, message]) => ({
      field,
      message: String(message),
      code: undefined,
    }))
  }

  return {
    message: error.Message,
    code: error.Code,
    fieldErrors,
    originalResponse: error,
  }
}

/**
 * Normalize Tax Injector error format to ApiErrorDetails
 * Flattens CsvRowErrors into fieldErrors with row context
 */
function normalizeTaxInjectorError(error: TaxInjectorErrorFormat): ApiErrorDetails {
  const fieldErrors: FieldError[] = []

  if (error.CsvRowErrors) {
    for (const rowError of error.CsvRowErrors) {
      for (const fieldError of rowError.FieldErrors) {
        fieldErrors.push({
          field: `Row ${rowError.RowNumber}: ${fieldError.Field}`,
          message: fieldError.Message,
          code: fieldError.Code,
        })
      }
    }
  }

  return {
    message: error.Message,
    code: error.Code,
    fieldErrors: fieldErrors.length > 0 ? fieldErrors : undefined,
    originalResponse: error,
  }
}

/**
 * Request context for error normalization
 */
export interface ErrorRequestContext {
  /** Correlation ID for tracing */
  traceId?: string
  /** Request URL */
  url?: string
  /** HTTP method */
  method?: string
}

/**
 * Extract the DataDog trace ID from Axios request config headers.
 * The trace ID is injected by the global Axios interceptor (setupAxiosTracing).
 */
function extractTraceIdFromAxiosConfig(config?: AxiosError['config']): string | undefined {
  if (!config?.headers) return undefined
  // AxiosHeaders supports get() method and bracket access
  const traceId = typeof config.headers.get === 'function'
    ? config.headers.get('x-datadog-trace-id')
    : (config.headers as Record<string, unknown>)['x-datadog-trace-id']
  return typeof traceId === 'string' ? traceId : undefined
}

/**
 * Build the full request URL from Axios config, handling baseURL + url combination.
 */
function extractUrlFromAxiosConfig(config?: AxiosError['config']): string | undefined {
  if (!config) return undefined
  if (config.baseURL && config.url) return `${config.baseURL}${config.url}`
  return config.baseURL || config.url || undefined
}

/**
 * Apply request context to ApiErrorDetails
 */
function applyContext(details: ApiErrorDetails, context?: ErrorRequestContext): ApiErrorDetails {
  if (!context) return details
  details.traceId = context.traceId
  details.url = context.url
  details.method = context.method
  return details
}

/**
 * Find the first string-valued property from an object by key priority
 */
function findStringProp(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    if (typeof obj[key] === 'string') return obj[key] as string
  }
  return undefined
}

/**
 * Find the first number-valued property from an object by key priority
 */
function findNumberProp(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    if (typeof obj[key] === 'number') return obj[key] as number
  }
  return undefined
}

/**
 * Normalize an Axios error to ApiErrorDetails
 */
function normalizeAxiosError(error: AxiosError, context?: ErrorRequestContext): ApiErrorDetails {
  // Extract request info from axios error if not provided
  const url = context?.url ?? extractUrlFromAxiosConfig(error.config)
  const method = context?.method ?? error.config?.method
  // Auto-extract traceId from DataDog trace headers injected by the global interceptor
  const traceId = context?.traceId ?? extractTraceIdFromAxiosConfig(error.config)
  const requestContext = { url, method, traceId }

  // If there's response data, try to normalize it
  if (error.response?.data) {
    const data = error.response.data as unknown
    const statusCode = error.response.status

    // Check for known error formats in response data
    if (isFCCError(data)) {
      return { ...normalizeFCCError(data), statusCode, ...requestContext }
    }

    if (isTaxInjectorError(data)) {
      return { ...normalizeTaxInjectorError(data), statusCode, ...requestContext }
    }

    if (isCommonMerchantError(data)) {
      return { ...normalizeCommonMerchantError(data), statusCode, ...requestContext }
    }

    // Try to extract message from response data
    if (typeof data === 'object' && data !== null) {
      const dataObj = data as Record<string, unknown>
      return {
        message: findStringProp(dataObj, 'message', 'error') ?? error.message,
        statusCode,
        originalResponse: data,
        ...requestContext,
      }
    }

    // If data is a string, use it as the message
    if (typeof data === 'string') {
      return {
        message: data || error.message,
        statusCode,
        originalResponse: data,
        ...requestContext,
      }
    }
  }

  // Network error (no response)
  if (!error.response) {
    return {
      message: error.message || 'Network error',
      code: 'NETWORK_ERROR',
      originalResponse: error,
      ...requestContext,
    }
  }

  // Generic HTTP error
  return {
    message: error.message || `HTTP ${error.response.status} error`,
    statusCode: error.response.status,
    originalResponse: error.response.data,
    ...requestContext,
  }
}

/**
 * Normalize any error into an ApiError.
 *
 * Handles:
 * - ApiError (returns as-is)
 * - FCC error format
 * - Common Merchant error format
 * - Tax Injector error format
 * - Axios errors
 * - Standard Error objects
 * - Unknown errors (converts to generic message)
 *
 * @param error - Any error value
 * @param context - Optional request context (correlation ID, URL, method)
 * @returns ApiError instance
 */
export function normalizeError(error: unknown, context?: ErrorRequestContext): ApiError {
  // Already an ApiError, return as-is (but update context if provided)
  if (ApiError.isApiError(error)) {
    if (context && !error.traceId) {
      // Create a new ApiError with the context if original doesn't have it
      return new ApiError({
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        fieldErrors: error.fieldErrors,
        originalResponse: error.originalResponse,
        traceId: context.traceId,
        url: context.url ?? error.url,
        method: context.method ?? error.method,
        timestamp: error.timestamp,
      })
    }
    return error
  }

  // Handle Axios errors first (they may contain formatted error data)
  if (isAxiosError(error)) {
    return new ApiError(normalizeAxiosError(error, context))
  }

  // Check for FCC error format
  if (isFCCError(error)) {
    return new ApiError(applyContext(normalizeFCCError(error), context))
  }

  // Check for Tax Injector error format (must check before Common Merchant)
  if (isTaxInjectorError(error)) {
    return new ApiError(applyContext(normalizeTaxInjectorError(error), context))
  }

  // Check for Common Merchant error format
  if (isCommonMerchantError(error)) {
    return new ApiError(applyContext(normalizeCommonMerchantError(error), context))
  }

  // Standard Error object
  if (error instanceof Error) {
    return new ApiError(applyContext({
      message: error.message,
      originalResponse: error,
    }, context))
  }

  // String error
  if (typeof error === 'string') {
    return new ApiError(applyContext({ message: error }, context))
  }

  // Generic object with message property (e.g., { message: "error text" })
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as Record<string, unknown>
    const message = findStringProp(errorObj, 'message', 'Message', 'error')

    if (message) {
      return new ApiError(applyContext({
        message,
        code: findStringProp(errorObj, 'code', 'Code'),
        statusCode: findNumberProp(errorObj, 'status', 'statusCode'),
        originalResponse: error,
      }, context))
    }
  }

  // Unknown error type
  return new ApiError(applyContext({
    message: 'An unexpected error occurred',
    originalResponse: error,
  }, context))
}
